import { useRef } from "react";
import { motion } from "framer-motion";
import { Droplets, Thermometer, Cog, Wind, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { INFRA_CARDS } from "@/lib/data";
import { Reveal } from "@/components/Reveal";

const ICONS = { droplets: Droplets, thermometer: Thermometer, cog: Cog, wind: Wind, shield: ShieldCheck };

export default function Solutions() {
  const trackRef = useRef(null);
  const scroll = (dir) => trackRef.current && trackRef.current.scrollBy({ left: dir * 310, behavior: "smooth" });

  return (
    <section id="solution" className="relative py-12 lg:py-16 bg-medis-navy text-white overflow-hidden" data-testid="solution-section">
      <div className="absolute inset-0 bg-grid-dark opacity-40" />
      <div className="absolute -top-32 -right-32 h-[400px] w-[400px] rounded-full bg-medis-blue/40 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 h-[400px] w-[400px] rounded-full bg-medis-teal/20 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <Reveal>
              <div className="text-[11.5px] uppercase tracking-[0.22em] text-medis-teal font-semibold mb-3">
                Cryogenic & Infrastructure Solutions
              </div>
              <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-[40px] leading-[1.1] tracking-tight">
                Reliable imaging needs more than the machine itself.
              </h2>
            </Reveal>
          </div>
          <p className="text-white/65 text-[14.5px] leading-relaxed max-w-sm">
            MEDIS supports the infrastructure behind MRI helium, pre-cooling, cryogenic components and shielding.
          </p>
        </div>

        <div className="relative">
          <button
            onClick={() => scroll(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 h-9 w-9 rounded-full border border-white/20 bg-white/10 backdrop-blur flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
            data-testid="solution-scroll-prev"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => scroll(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 h-9 w-9 rounded-full border border-white/20 bg-white/10 backdrop-blur flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
            data-testid="solution-scroll-next"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          <div ref={trackRef} className="flex gap-4 px-10 overflow-x-auto snap-x snap-mandatory" style={{ scrollbarWidth: "none" }}>
            {INFRA_CARDS.map((card, i) => {
              const Icon = i === 0 ? ICONS.droplets : i === 1 ? ICONS.thermometer : i === 2 ? ICONS.cog : i === 3 ? ICONS.wind : ICONS.shield;
              return (
                <motion.div
                  key={card.num}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: Math.min(i * 0.08, 0.4) }}
                  className="infra-card flex-shrink-0 snap-start rounded-xl border border-white/10 bg-white/[0.05] backdrop-blur-sm p-6 relative group overflow-hidden cursor-default"
                  style={{ width: 280 }}
                  data-testid={`cryo-card-${card.num}`}
                >
                  <div className="absolute top-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: card.accent }} />
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"
                    style={{ background: `radial-gradient(circle at 30% 20%, ${card.accent}15, transparent 70%)` }}
                  />
                  <div
                    className="relative text-[22px] font-bold tracking-widest mb-4 opacity-40 group-hover:opacity-100 transition-opacity duration-200"
                    style={{ color: card.accent, fontFamily: "Sora, Inter, sans-serif" }}
                  >
                    {card.num}
                  </div>
                  <div className="relative inline-flex h-11 w-11 items-center justify-center rounded-lg mb-5" style={{ background: `${card.accent}20` }}>
                    <Icon className="h-5 w-5" style={{ color: card.accent }} />
                  </div>
                  <h3 className="relative font-display font-semibold text-white text-[15.5px] leading-snug mb-2">{card.title}</h3>
                  <p className="relative text-[12.5px] text-white/55 leading-relaxed">{card.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
